import { defaultSettings, createUserSettings } from '../config/defaultSettings';

const SETTINGS_KEY = 'modiq-settings';

class SettingsManager {
  constructor() {
    this.settings = this.loadSettings();
    this.listeners = new Set();
  }

  // Load settings from localStorage or use defaults
  loadSettings() {
    try {
      const stored = localStorage.getItem(SETTINGS_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge with defaults to ensure all keys exist
        return this.mergeWithDefaults(parsed);
      }
    } catch (error) {
      console.warn('Failed to load settings from localStorage:', error);
    }
    return { ...defaultSettings };
  }

  // Merge stored settings with defaults to handle new settings
  mergeWithDefaults(stored) {
    const merged = { ...defaultSettings };
    
    // Deep merge stored settings
    Object.keys(stored).forEach(key => {
      if (typeof stored[key] === 'object' && stored[key] !== null && !Array.isArray(stored[key])) {
        merged[key] = { ...defaultSettings[key], ...stored[key] };
      } else {
        merged[key] = stored[key];
      }
    });
    
    return merged;
  }

  // Save settings to localStorage
  saveSettings() {
    try {
      const toSave = {
        ...this.settings,
        lastUpdated: new Date().toISOString()
      };
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(toSave));
      this.notifyListeners();
    } catch (error) {
      console.error('Failed to save settings to localStorage:', error);
    }
  }

  // Get a setting value by path (e.g., 'boxDimensions.width')
  get(path) {
    return path.split('.').reduce((obj, key) => obj?.[key], this.settings);
  }

  // Alias for get method to match usage in components
  getSetting(path) {
    return this.get(path);
  }

  // Set a setting value by path
  set(path, value) {
    const keys = path.split('.');
    const lastKey = keys.pop();
    const target = keys.reduce((obj, key) => {
      if (!obj[key]) obj[key] = {};
      return obj[key];
    }, this.settings);
    
    target[lastKey] = value;
    this.saveSettings();
  }

  // Alias for set method to match usage in components
  updateSetting(path, value) {
    return this.set(path, value);
  }

  // Update multiple settings at once
  update(updates) {
    Object.keys(updates).forEach(path => {
      this.set(path, updates[path]);
    });
  }

  // Get all settings
  getAll() {
    return { ...this.settings };
  }

  // Reset to defaults
  reset() {
    this.settings = { ...defaultSettings };
    this.saveSettings();
  }

  // Reset specific section
  resetSection(section) {
    if (defaultSettings[section]) {
      this.settings[section] = { ...defaultSettings[section] };
      this.saveSettings();
    }
  }

  // Subscribe to settings changes
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  // Notify all listeners of changes
  notifyListeners() {
    this.listeners.forEach(callback => {
      try {
        callback(this.settings);
      } catch (error) {
        console.error('Settings listener error:', error);
      }
    });
  }

  // Export settings as JSON
  export() {
    return JSON.stringify(this.settings, null, 2);
  }

  // Import settings from JSON
  import(jsonString) {
    try {
      const imported = JSON.parse(jsonString);
      this.settings = this.mergeWithDefaults(imported);
      this.saveSettings();
      return true;
    } catch (error) {
      console.error('Failed to import settings:', error);
      return false;
    }
  }
}

// Create singleton instance
const settingsManager = new SettingsManager();

export default settingsManager; 